"use strict";
/**
 * Win-probability API — model output for a live game.
 *
 * Browser → proxy server (/win-prob/*). The server's tracker owns the ESPN
 * feed and model; the QB event command goes to that same tracker.
 * Nothing here is on the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.postTaps = exports.fetchTaps = exports.fetchServerClock = void 0;
exports.fetchWinProbLeagues = fetchWinProbLeagues;
exports.fetchWinProbGames = fetchWinProbGames;
exports.fetchWinProbGame = fetchWinProbGame;
exports.trackWinProbGame = trackWinProbGame;
exports.recordWinProbPersonnel = recordWinProbPersonnel;
exports.postHockeyEvent = postHockeyEvent;
exports.calibrateHockeyGame = calibrateHockeyGame;
exports.anchorHockeyGame = anchorHockeyGame;
exports.fetchHockeySeries = fetchHockeySeries;
exports.setKillSwitch = setKillSwitch;
const request_1 = require("./request");
/** The leagues this server's page can open (production and the recorder differ). */
async function fetchWinProbLeagues() {
    const response = await (0, request_1.apiRequest)("GET", "/win-prob/leagues");
    return response.data ?? [];
}
/** Today's games for a league, each flagged with whether the tracker follows it. */
async function fetchWinProbGames(league) {
    const response = await (0, request_1.apiRequest)("GET", "/win-prob/games", {
        params: { league },
    });
    return { games: response.data ?? [], feedStale: Boolean(response.feedStale) };
}
/**
 * The tracker's latest snapshot for one game. In-memory read on the server, so
 * it is cheap to poll; 404 means the game is not being tracked.
 */
async function fetchWinProbGame(league, gameId, opts = {}) {
    const response = await (0, request_1.apiRequest)("GET", `/win-prob/games/${encodeURIComponent(league)}/${encodeURIComponent(gameId)}`, opts.presence ? { params: { presence: opts.presence } } : {});
    return response.data;
}
/** Start tracking a game the scheduler has not picked up yet. */
async function trackWinProbGame(league, gameId) {
    await (0, request_1.apiRequest)("POST", `/win-prob/games/${encodeURIComponent(league)}/${encodeURIComponent(gameId)}/track`);
}
async function recordWinProbPersonnel(league, gameId, event) {
    const response = await (0, request_1.apiRequest)("POST", `/win-prob/games/${encodeURIComponent(league)}/${encodeURIComponent(gameId)}/personnel`, { body: event });
    return response.data;
}
// The tap pads' calls live in api/taps.ts, which the NBA recorder ships with alone.
var taps_1 = require("./taps");
Object.defineProperty(exports, "fetchServerClock", { enumerable: true, get: function () { return taps_1.fetchServerClock; } });
Object.defineProperty(exports, "fetchTaps", { enumerable: true, get: function () { return taps_1.fetchTaps; } });
Object.defineProperty(exports, "postTaps", { enumerable: true, get: function () { return taps_1.postTaps; } });
// --- hockey pad (docs/nhl-model/PLAN.md §4.3, §4.5) ---
/** One state-changing event; idempotent on clientEventId, so a retry is safe. The reply is the game's new view. */
async function postHockeyEvent(gameId, event) {
    const response = await (0, request_1.apiRequest)("POST", `/win-prob/games/nhl/${encodeURIComponent(gameId)}/events`, { body: event });
    return response.data;
}
/** The operator's prices for one game → the solved rates, stored for that game. */
async function calibrateHockeyGame(gameId, odds, gameType) {
    const response = await (0, request_1.apiRequest)("POST", `/win-prob/games/nhl/${encodeURIComponent(gameId)}/pregame`, { body: { odds, gameType } });
    return response.data;
}
/** One tap: re-solve the game from the sportsbook line or the exchange's book already on record. */
async function anchorHockeyGame(gameId, anchor, gameType) {
    const response = await (0, request_1.apiRequest)("POST", `/win-prob/games/nhl/${encodeURIComponent(gameId)}/pregame`, { body: { anchor, gameType } });
    return response.data;
}
/** The game so far from the box's database, for the portrait view's chart and event log; null until the game has a row. */
async function fetchHockeySeries(gameId) {
    const response = await (0, request_1.apiRequest)("GET", `/win-prob/games/nhl/${encodeURIComponent(gameId)}/series`);
    return response.data ?? null;
}
/** The global kill switch (null gameId) or one game's. Never refused, never confirmed. */
async function setKillSwitch(on, league = null, gameId = null) {
    const path = league && gameId ? `/win-prob/games/${encodeURIComponent(league)}/${encodeURIComponent(gameId)}/kill` : "/win-prob/kill";
    const response = await (0, request_1.apiRequest)("POST", path, { body: { on } });
    return response.data;
}
